'use client';

import type { Editor} from '@tiptap/react';
import { EditorContent } from '@tiptap/react';
import { useState } from 'react';
import styled from 'styled-components';

import { FeedbackEmojiPlugin } from '@zoomit/editor';
import { EyeSlash, Mention, Redo, Sticker, Undo } from '@zoomit/icons';
import { Box, Flex, IconButton, Popover, Spacing } from '@zoomit/primitives';
import { isMobile } from '@zoomit/web-kit';
import { cssVar, theme, Typography } from '@zoomit/zoomit-ds';

import styles from './FeedbackEditor.module.scss';
import { MentionList } from './MentionList';
import { StickerPicker } from './StickerPicker';

type Props = {
  maxLength: number;
  editor: Editor | null;
};

export const FeedbackEditor = (props: Props) => {
  const { maxLength, editor } = props;

  const [isStickerPickerOpen, setIsStickerPickerOpen] = useState(false);

  if (!editor) return null;
  return (
    <Box className={styles.root}>
      <EditorContent editor={editor} />
      {isMobile && (
        <Typography
          variant="callout"
          tag="p"
          spacing={{ pl: 'x4', pb: 'x2' }}
          textAlign="left"
          className="fa"
        >
          {maxLength - editor.storage.characterCount.characters()}
        </Typography>
      )}
      <PluginsWrapper
        alignItems="center"
        spacing={{ p: Spacing.x2 }}
        flexWrap="nowrap"
        justifyContent="space-between"
      >
        <Flex
          gap={2}
          style={{ width: '100%' }}
          alignItems="center"
          justifyContent={isMobile ? 'space-evenly' : 'flex-start'}
        >
          <TooltipButton content="ایموجی">
            <FeedbackEmojiPlugin editor={editor} />
          </TooltipButton>
          <TooltipButton content="اشاره به">
            <Mention
              fill={editor.isActive('mention') ? cssVar(theme.primary1) : cssVar(theme.grey1)}
              size="dl"
              onClick={() => editor?.chain().insertContent('@').focus().run()}
            />
          </TooltipButton>
          <TooltipButton content="اسپویل">
            <EyeSlash
              fill={editor.isActive('spoiler') ? cssVar(theme.primary1) : cssVar(theme.grey1)}
              size="dl"
              onClick={() => editor?.commands.toggleSpoiler()}
            />
          </TooltipButton>
          <TooltipButton content="استیکر">
            <IconButton onClick={() => setIsStickerPickerOpen(true)} className={'relative'}>
              <Sticker size="dl" />
              <Popover
                triggerOffset={25}
                isOpen={isStickerPickerOpen}
                setOpen={(value) => setIsStickerPickerOpen(value)}
                placement={isMobile ? 'bottom' : 'bottom-end'}
                possiblePlacements={['bottom-end', 'top-end']}
                content={
                  <StickerPicker
                    select={({ imageId }) => {
                      let stickerCount = 0;
                      editor.state.doc.descendants((node) => {
                        if (node.type.name === 'sticker') {
                          stickerCount++;
                        }
                      });

                      stickerCount < 6 && editor.commands.addSticker({ stickerId: imageId });
                    }}
                  />
                }
              />
            </IconButton>
          </TooltipButton>
          {isMobile && <Hr />}
          <TooltipButton onClick={() => editor?.commands.undo()} content="Undo">
            <Undo />
          </TooltipButton>
          <TooltipButton onClick={() => editor?.commands.redo()} content="Redo">
            <Redo />
          </TooltipButton>
          <MentionList editor={editor} />
        </Flex>
        {!isMobile && (
          <Typography className="fa">{maxLength - editor.storage.characterCount.characters()}</Typography>
        )}
      </PluginsWrapper>
    </Box>
  );
};

const Hr = styled.div`
  height: 16px;
  width: 1px;
  background-color: var(${theme.grey3});
`;

const PluginsWrapper = styled(Flex)`
  width: 100%;
  background-color: var(${theme.grey7});
`;

const TooltipButton = styled(IconButton)<{ content: string }>`
  position: relative;

  &:hover::before {
    opacity: 1;
  }

  &::before {
    user-select: none;
    display: inline-block;
    width: max-content;
    pointer-events: none;
    position: absolute;
    opacity: 0;
    transition: opacity 0.2s;
    left: 50%;
    transform: translateX(-50%);
    top: -3.6rem;
    content: '${({ content }) => content}';
    color: var(${theme.white});
    background-color: var(${theme.black1});
    font-size: 1.2rem;
    padding: 1rem 0.6rem;
    border-radius: var(${theme.radius.sm});
  }
`;
